/**
 * User Experience Testing and Refinement System
 * Provides tools for testing animation UX, gathering feedback, and optimizing user flow
 */

export interface UXTestSession {
  id: string;
  userId?: string;
  startTime: number;
  endTime?: number;
  userAgent: string;
  screenSize: { width: number; height: number };
  interactions: UXInteraction[];
  feedback: UXFeedback[];
  metrics: UXMetrics;
}

export interface UXInteraction {
  timestamp: number;
  type: 'click' | 'hover' | 'scroll' | 'focus' | 'gesture';
  element: string;
  position: { x: number; y: number };
  duration?: number;
  animationTriggered?: string;
  userSatisfaction?: number; // 1-5 scale
}

export interface UXFeedback {
  timestamp: number;
  type: 'animation_speed' | 'animation_smoothness' | 'visual_appeal' | 'usability' | 'accessibility';
  rating: number; // 1-5 scale
  comment?: string;
  element?: string;
  suggestion?: string;
}

export interface UXMetrics {
  totalInteractions: number;
  averageInteractionTime: number;
  animationCompletionRate: number;
  userSatisfactionScore: number;
  taskCompletionTime: number;
  errorRate: number;
  accessibilityScore: number;
}

export interface AnimationRefinement {
  element: string;
  property: string;
  originalValue: any;
  suggestedValue: any;
  reason: string;
  confidence: number; // 0-1
  userFeedbackBased: boolean;
}

class UXTestingManager {
  private currentSession: UXTestSession | null = null;
  private sessions: Map<string, UXTestSession> = new Map();
  private refinements: Map<string, AnimationRefinement[]> = new Map();
  private feedbackCollectors: Map<string, HTMLElement> = new Map();

  constructor() {
    this.initializeEventListeners();
    this.loadStoredSessions();
  }

  private initializeEventListeners(): void {
    // Track user interactions
    document.addEventListener('click', this.handleInteraction.bind(this));
    document.addEventListener('mouseover', this.handleInteraction.bind(this));
    document.addEventListener('scroll', this.handleInteraction.bind(this));
    document.addEventListener('focusin', this.handleInteraction.bind(this));

    // Track animation events
    document.addEventListener('animationstart', this.handleAnimationEvent.bind(this));
    document.addEventListener('animationend', this.handleAnimationEvent.bind(this));
    document.addEventListener('transitionstart', this.handleAnimationEvent.bind(this));
    document.addEventListener('transitionend', this.handleAnimationEvent.bind(this));

    // Track page visibility for session management
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  private handleInteraction(event: Event): void {
    if (!this.currentSession) return;

    const interaction: UXInteraction = {
      timestamp: Date.now(),
      type: event.type as any,
      element: this.getElementSelector(event.target as HTMLElement),
      position: this.getEventPosition(event),
      duration: 0
    };

    // Check if interaction triggered an animation
    const animatedElement = this.findAnimatedElement(event.target as HTMLElement);
    if (animatedElement) {
      interaction.animationTriggered = this.getElementSelector(animatedElement);
    }

    this.currentSession.interactions.push(interaction);
    this.updateSessionMetrics();
  }

  private handleAnimationEvent(event: AnimationEvent | TransitionEvent): void {
    if (!this.currentSession) return;

    // Find related interaction
    const recentInteractions = this.currentSession.interactions
      .filter(i => Date.now() - i.timestamp < 2000)
      .reverse();

    const relatedInteraction = recentInteractions.find(i => 
      i.element === this.getElementSelector(event.target as HTMLElement) ||
      i.animationTriggered === this.getElementSelector(event.target as HTMLElement)
    );

    if (relatedInteraction && event.type.endsWith('end')) {
      relatedInteraction.duration = Date.now() - relatedInteraction.timestamp;
    }
  }

  private handleVisibilityChange(): void {
    if (document.hidden && this.currentSession) {
      this.endSession();
    } else if (!document.hidden && !this.currentSession) {
      this.startSession();
    }
  }

  private getElementSelector(element: HTMLElement): string {
    if (!element) return 'unknown';
    
    // Try to get a meaningful selector
    if (element.id) return `#${element.id}`;
    if (element.className) return `.${element.className.split(' ')[0]}`;
    return element.tagName.toLowerCase();
  }

  private getEventPosition(event: Event): { x: number; y: number } {
    if (event instanceof MouseEvent) {
      return { x: event.clientX, y: event.clientY };
    }
    if (event instanceof TouchEvent && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: 0, y: 0 };
  }

  private findAnimatedElement(element: HTMLElement): HTMLElement | null {
    // Check if element or its parents have animations
    let current = element;
    while (current && current !== document.body) {
      const style = getComputedStyle(current);
      if (style.animation !== 'none' || style.transition !== 'none') {
        return current;
      }
      current = current.parentElement!;
    }
    return null;
  }

  private updateSessionMetrics(): void {
    if (!this.currentSession) return;

    const interactions = this.currentSession.interactions;
    const metrics: UXMetrics = {
      totalInteractions: interactions.length,
      averageInteractionTime: this.calculateAverageInteractionTime(interactions),
      animationCompletionRate: this.calculateAnimationCompletionRate(interactions),
      userSatisfactionScore: this.calculateSatisfactionScore(interactions),
      taskCompletionTime: Date.now() - this.currentSession.startTime,
      errorRate: this.calculateErrorRate(interactions),
      accessibilityScore: this.calculateAccessibilityScore(interactions)
    };

    this.currentSession.metrics = metrics;
  }

  private calculateAverageInteractionTime(interactions: UXInteraction[]): number {
    const timedInteractions = interactions.filter(i => i.duration && i.duration > 0);
    if (timedInteractions.length === 0) return 0;
    
    const totalTime = timedInteractions.reduce((sum, i) => sum + (i.duration || 0), 0);
    return totalTime / timedInteractions.length;
  }

  private calculateAnimationCompletionRate(interactions: UXInteraction[]): number {
    const animationInteractions = interactions.filter(i => i.animationTriggered);
    if (animationInteractions.length === 0) return 1;
    
    const completedAnimations = animationInteractions.filter(i => i.duration && i.duration > 0);
    return completedAnimations.length / animationInteractions.length;
  }

  private calculateSatisfactionScore(interactions: UXInteraction[]): number {
    const ratedInteractions = interactions.filter(i => i.userSatisfaction);
    if (ratedInteractions.length === 0) return 3; // Neutral default
    
    const totalRating = ratedInteractions.reduce((sum, i) => sum + (i.userSatisfaction || 0), 0);
    return totalRating / ratedInteractions.length;
  }

  private calculateErrorRate(interactions: UXInteraction[]): number {
    // Define error patterns (rapid clicks, back-and-forth movements, etc.)
    let errors = 0;
    
    for (let i = 1; i < interactions.length; i++) {
      const current = interactions[i];
      const previous = interactions[i - 1];
      
      // Rapid clicking on same element
      if (current.element === previous.element && 
          current.timestamp - previous.timestamp < 300 &&
          current.type === 'click' && previous.type === 'click') {
        errors++;
      }
      
      // Quick back-and-forth between elements
      if (i > 1) {
        const beforePrevious = interactions[i - 2];
        if (current.element === beforePrevious.element &&
            current.timestamp - beforePrevious.timestamp < 1000) {
          errors++;
        }
      }
    }
    
    return interactions.length > 0 ? errors / interactions.length : 0;
  }

  private calculateAccessibilityScore(interactions: UXInteraction[]): number {
    let score = 1.0;
    
    // Check for keyboard navigation usage
    const keyboardInteractions = interactions.filter(i => i.type === 'focus');
    const totalInteractions = interactions.length;
    
    if (totalInteractions > 0) {
      const keyboardUsageRatio = keyboardInteractions.length / totalInteractions;
      // Higher keyboard usage indicates better accessibility
      score *= (1 + keyboardUsageRatio * 0.2);
    }
    
    // Check for reduced motion compliance
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      // Check if animations are still running (accessibility violation)
      const animationInteractions = interactions.filter(i => i.animationTriggered);
      if (animationInteractions.length > 0) {
        score *= 0.5; // Penalty for not respecting reduced motion
      }
    }
    
    return Math.min(score, 1.0);
  }

  private loadStoredSessions(): void {
    try {
      const stored = localStorage.getItem('ux-test-sessions');
      if (stored) {
        const sessions = JSON.parse(stored);
        sessions.forEach((session: UXTestSession) => {
          this.sessions.set(session.id, session);
        });
      }
    } catch (error) {
      console.warn('Failed to load stored UX sessions:', error);
    }
  }

  private saveSession(session: UXTestSession): void {
    this.sessions.set(session.id, session);
    
    try {
      const sessionsArray = Array.from(this.sessions.values());
      // Keep only last 10 sessions to avoid storage bloat
      const recentSessions = sessionsArray.slice(-10);
      localStorage.setItem('ux-test-sessions', JSON.stringify(recentSessions));
    } catch (error) {
      console.warn('Failed to save UX session:', error);
    }
  }

  // Public API
  public startSession(userId?: string): string {
    const sessionId = `ux-session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    this.currentSession = {
      id: sessionId,
      userId,
      startTime: Date.now(),
      userAgent: navigator.userAgent,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      interactions: [],
      feedback: [],
      metrics: {
        totalInteractions: 0,
        averageInteractionTime: 0,
        animationCompletionRate: 0,
        userSatisfactionScore: 3,
        taskCompletionTime: 0,
        errorRate: 0,
        accessibilityScore: 1
      }
    };

    return sessionId;
  }

  public endSession(): UXTestSession | null {
    if (!this.currentSession) return null;

    this.currentSession.endTime = Date.now();
    this.updateSessionMetrics();
    
    const session = { ...this.currentSession };
    this.saveSession(session);
    this.currentSession = null;

    return session;
  }

  public addFeedback(feedback: Omit<UXFeedback, 'timestamp'>): void {
    if (!this.currentSession) return;

    this.currentSession.feedback.push({
      ...feedback,
      timestamp: Date.now()
    });

    this.updateSessionMetrics();
  }

  public rateLastInteraction(rating: number): void {
    if (!this.currentSession || this.currentSession.interactions.length === 0) return;

    const lastInteraction = this.currentSession.interactions[this.currentSession.interactions.length - 1];
    lastInteraction.userSatisfaction = Math.max(1, Math.min(5, rating));
    
    this.updateSessionMetrics();
  }

  public getCurrentSession(): UXTestSession | null {
    return this.currentSession;
  }

  public getAllSessions(): UXTestSession[] {
    return Array.from(this.sessions.values());
  }

  public generateRefinements(): AnimationRefinement[] {
    const allSessions = this.getAllSessions();
    const refinements: AnimationRefinement[] = [];

    // Analyze sessions for improvement opportunities
    const elementMetrics = this.analyzeElementPerformance(allSessions);
    
    for (const [element, metrics] of elementMetrics) {
      // Suggest timing refinements based on user feedback
      if (metrics.averageSatisfaction < 3) {
        if (metrics.averageDuration > 500) {
          refinements.push({
            element,
            property: 'animation-duration',
            originalValue: `${metrics.averageDuration}ms`,
            suggestedValue: `${Math.max(200, metrics.averageDuration * 0.8)}ms`,
            reason: 'Users find animation too slow',
            confidence: 0.8,
            userFeedbackBased: true
          });
        }
        
        if (metrics.errorRate > 0.1) {
          refinements.push({
            element,
            property: 'animation-timing-function',
            originalValue: 'ease',
            suggestedValue: 'ease-out',
            reason: 'High error rate suggests confusing timing',
            confidence: 0.6,
            userFeedbackBased: true
          });
        }
      }

      // Suggest accessibility improvements
      if (metrics.accessibilityScore < 0.8) {
        refinements.push({
          element,
          property: 'prefers-reduced-motion',
          originalValue: 'not implemented',
          suggestedValue: 'respect user preference',
          reason: 'Low accessibility score',
          confidence: 0.9,
          userFeedbackBased: false
        });
      }
    }

    return refinements;
  }

  private analyzeElementPerformance(sessions: UXTestSession[]): Map<string, any> {
    const elementMetrics = new Map();

    sessions.forEach(session => {
      session.interactions.forEach(interaction => {
        if (!interaction.animationTriggered) return;

        const element = interaction.animationTriggered;
        if (!elementMetrics.has(element)) {
          elementMetrics.set(element, {
            interactions: [],
            satisfactionRatings: [],
            durations: [],
            errors: 0
          });
        }

        const metrics = elementMetrics.get(element);
        metrics.interactions.push(interaction);
        
        if (interaction.userSatisfaction) {
          metrics.satisfactionRatings.push(interaction.userSatisfaction);
        }
        
        if (interaction.duration) {
          metrics.durations.push(interaction.duration);
        }
      });
    });

    // Calculate aggregated metrics
    for (const [element, data] of elementMetrics) {
      const avgSatisfaction = data.satisfactionRatings.length > 0 
        ? data.satisfactionRatings.reduce((a: number, b: number) => a + b, 0) / data.satisfactionRatings.length
        : 3;
      
      const avgDuration = data.durations.length > 0
        ? data.durations.reduce((a: number, b: number) => a + b, 0) / data.durations.length
        : 0;

      const errorRate = sessions.reduce((sum, session) => sum + session.metrics.errorRate, 0) / sessions.length;
      const accessibilityScore = sessions.reduce((sum, session) => sum + session.metrics.accessibilityScore, 0) / sessions.length;

      elementMetrics.set(element, {
        ...data,
        averageSatisfaction: avgSatisfaction,
        averageDuration: avgDuration,
        errorRate,
        accessibilityScore
      });
    }

    return elementMetrics;
  }

  public createFeedbackWidget(elementSelector: string): HTMLElement {
    const widget = document.createElement('div');
    widget.className = 'ux-feedback-widget';
    widget.innerHTML = `
      <div class="feedback-prompt">
        <p>How was this animation?</p>
        <div class="rating-buttons">
          <button data-rating="1">😞</button>
          <button data-rating="2">😐</button>
          <button data-rating="3">🙂</button>
          <button data-rating="4">😊</button>
          <button data-rating="5">🤩</button>
        </div>
        <button class="close-feedback">×</button>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .ux-feedback-widget {
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        padding: 16px;
        z-index: 10000;
        font-family: system-ui, sans-serif;
        max-width: 300px;
      }
      
      .feedback-prompt p {
        margin: 0 0 12px 0;
        font-size: 14px;
        color: #333;
      }
      
      .rating-buttons {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;
      }
      
      .rating-buttons button {
        background: none;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 8px;
        cursor: pointer;
        font-size: 18px;
        transition: all 0.2s ease;
      }
      
      .rating-buttons button:hover {
        background: #f0f0f0;
        transform: scale(1.1);
      }
      
      .close-feedback {
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: #999;
      }
    `;
    
    if (!document.querySelector('#ux-feedback-styles')) {
      style.id = 'ux-feedback-styles';
      document.head.appendChild(style);
    }

    // Add event listeners
    widget.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      if (target.hasAttribute('data-rating')) {
        const rating = parseInt(target.getAttribute('data-rating')!);
        this.addFeedback({
          type: 'animation_smoothness',
          rating,
          element: elementSelector
        });
        widget.remove();
      }
      
      if (target.classList.contains('close-feedback')) {
        widget.remove();
      }
    });

    this.feedbackCollectors.set(elementSelector, widget);
    return widget;
  }

  public showFeedbackWidget(elementSelector: string): void {
    // Remove existing widget if present
    const existing = this.feedbackCollectors.get(elementSelector);
    if (existing && existing.parentNode) {
      existing.remove();
    }

    // Create and show new widget
    const widget = this.createFeedbackWidget(elementSelector);
    document.body.appendChild(widget);

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (widget.parentNode) {
        widget.remove();
      }
    }, 10000);
  }

  public exportSessionData(format: 'json' | 'csv' = 'json'): string {
    const sessions = this.getAllSessions();
    
    if (format === 'csv') {
      return this.exportToCSV(sessions);
    }
    
    return JSON.stringify(sessions, null, 2);
  }

  private exportToCSV(sessions: UXTestSession[]): string {
    const headers = [
      'Session ID', 'User ID', 'Start Time', 'Duration (ms)', 'Total Interactions',
      'Avg Interaction Time', 'Animation Completion Rate', 'User Satisfaction',
      'Error Rate', 'Accessibility Score'
    ];

    const rows = sessions.map(session => [
      session.id,
      session.userId || 'anonymous',
      new Date(session.startTime).toISOString(),
      session.endTime ? session.endTime - session.startTime : 'ongoing',
      session.metrics.totalInteractions,
      session.metrics.averageInteractionTime.toFixed(2),
      session.metrics.animationCompletionRate.toFixed(2),
      session.metrics.userSatisfactionScore.toFixed(2),
      session.metrics.errorRate.toFixed(2),
      session.metrics.accessibilityScore.toFixed(2)
    ]);

    return [headers, ...rows].map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
  }
}

// Export singleton instance
export const uxTester = new UXTestingManager();

// Utility functions
export const startUXSession = (userId?: string) => uxTester.startSession(userId);
export const endUXSession = () => uxTester.endSession();
export const addUXFeedback = (feedback: Omit<UXFeedback, 'timestamp'>) => uxTester.addFeedback(feedback);
export const rateAnimation = (rating: number) => uxTester.rateLastInteraction(rating);
export const showAnimationFeedback = (elementSelector: string) => uxTester.showFeedbackWidget(elementSelector);
export const generateAnimationRefinements = () => uxTester.generateRefinements();
export const exportUXData = (format: 'json' | 'csv' = 'json') => uxTester.exportSessionData(format);